import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Link } from 'components/Link';
import { WorkoutActions } from 'components/WorkoutActions';
import { Card } from 'styleguide/Card';
import { Screen } from 'styleguide/ScreenContainer';
import { H5 } from 'styleguide/Texts';
import { Exercise } from './components/Exercise';
import { UserCard } from './components/UserCard';
import { UserHeader } from './components/UserHeader';
import { WorkoutDetailViewModel } from './WorkoutDetailViewModel';
import { sendEvent } from 'utils/events';
import { Stats } from 'components/WorkoutCell/components/Stats';
import { modal } from 'components/Modals/ModalManager';
import { Spacing } from 'styleguide/spacing';
import { LinkableText } from 'components/LinkableText';
import { copyToClipboard } from 'utils/clipboard';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { colors } from 'styleguide/colors';
import { View } from 'styleguide/View';
import { WorkoutNotFoundScreen } from './WorkoutNotFoundScreen';
import { DividerLine } from 'styleguide/DividerLine';
import { WorkoutContent } from 'components/WorkoutCell/components/WorkoutContent';
import { t } from 'translations/t';

interface WorkoutDetailScreenProps {
  urlWorkoutId: string;
}

export const WorkoutDetailScreen = observer(({ urlWorkoutId }: WorkoutDetailScreenProps) => {
  const [vm] = useState(new WorkoutDetailViewModel());

  useEffect(() => {
    vm.fetchWorkout(urlWorkoutId);
  }, [urlWorkoutId]);

  useEffect(() => {
    setTimeout(() => {
      /**
       * Without setTimeout this event is fired before
       * amplitude has been initialized.
       */
      sendEvent('workoutDetail_screenview', {
        authenticated: true,
      });
    });
  }, []);

  const { userData, viewState } = vm;

  const {
    username,
    profilePic,
    description,
    workoutCount,
    followerCount,
    followingCount,
    showFollowButton,
  } = userData;

  return (
    <Screen>
      <Content>
        <LeftContainer>
          {viewState.type === 'loading' ? (
            <BlankContainer>
              <ClipLoader size={30} color={colors.primary} loading={true} />
            </BlankContainer>
          ) : viewState.type === 'loaded' ? (
            <View style={{ gap: Spacing.md }}>
              <TopContainer>
                <Link href={`/user/${username}`}>
                  <UserHeader
                    username={username}
                    profileImage={profilePic}
                    date={viewState.data.date}
                    isPrivateWorkout={viewState.data.isPrivate}
                    isCoached={userData.isCoached}
                  />
                </Link>

                <View>
                  <H5>{viewState.data.title}</H5>
                  {!!viewState.data.description && (
                    <LinkableText enableMentions text={viewState.data.description} />
                  )}
                </View>

                <Stats stats={viewState.data.stats} />

                {viewState.data.mediaCells.length > 0 && (
                  <MediaContainer>
                    <DividerLine />
                    <WorkoutContent cells={viewState.data.mediaCells} />
                  </MediaContainer>
                )}
              </TopContainer>
              <WorkoutActions
                isLiked={viewState.data.isLiked}
                onLike={viewState.data.onLikeWorkout}
                onShowLikes={() =>
                  modal.showUserListModal({ mode: 'likes', workoutId: viewState.data.id })
                }
                likeCount={viewState.data.likeCount}
                onComment={() => modal.openCommentModal(viewState.data.id)}
                commentCount={viewState.data.commentCount}
                onShare={() => {
                  sendEvent('workoutDetailScreen_workoutCell_sharePress');
                  copyToClipboard(`https://${location.hostname}/workout/${viewState.data.id}`);
                  toast.success(t('web.commentModal.copiedLink'));
                }}
              />
              <BottomContainer>
                {viewState.data.exercises.map(e => {
                  return (
                    <Exercise
                      key={e.key}
                      image={e.image}
                      title={e.title}
                      supersetId={e.supersetId}
                      notes={e.notes}
                      secondColumnHeader={e.secondColumnHeader}
                      sets={e.sets}
                      onExerciseClick={e.onExerciseClick}
                      restTimeSeconds={e.restTimeSeconds}
                    />
                  );
                })}
              </BottomContainer>
            </View>
          ) : (
            <WorkoutNotFoundScreen />
          )}
        </LeftContainer>

        <RightContainer>
          <UserCard
            profileImage={profilePic}
            username={username}
            description={description}
            workoutCount={workoutCount}
            followerCount={followerCount}
            followingCount={followingCount}
            showFollowButton={showFollowButton}
          />
        </RightContainer>
      </Content>
    </Screen>
  );
});

const Content = styled.div`
  display: grid;
  max-width: 1024px;
  justify-content: center;
  grid-template-columns: 5fr 3fr;
  gap: 20px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeftContainer = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const RightContainer = styled(View)`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const TopContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-bottom: 0px;
`;

const MediaContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: 0px;
`;

const BottomContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-top: ${Spacing.md}px;
`;

const BlankContainer = styled(View)`
  height: 500px;
  align-items: center;
  justify-content: center;
`;
